<template>
  <div class="row gap-8">
    <div :class="['relative-position', wideClass]">
      <DateInput
        ref="dateRef"
        v-model="date"
        :with-time="combinedDatetimeField"
        :time="time"
        :label="dateLabel"
        :min-date="minDate"
        :max-date="maxDate"
        hide-bottom-space
        :rules="[ ...rules, dateValidation ]"
        :lazy-rules="lazyRules"
        :clearable="false"
        :stack-label="!combinedDatetimeField"
        :default-year-month="defaultYearMonth"
        outlined
        @update:modelValue="input"
        @update:timeValue="onUpdateCombinedTime"
        @focus="$emit('focus-date')"
        @focus-time="$emit('focus-time')"
      >
        <template #append>
          <q-btn
            v-if="useNow && combinedDatetimeField"
            class="text-weight-medium"
            dense
            unelevated
            color="grey-4"
            text-color="grey-10"
            padding="2px 12px"
            rounded
            :label="$t('button.now')"
            @click.stop="setNowDate"
          />
        </template>
      </DateInput>
    </div>
    <div
      v-if="!combinedDatetimeField"
      class="col relative-position"
    >
      <TimeField
        ref="timeRef"
        v-model="time"
        :label="timeLabel"
        hide-bottom-space
        :rules="[ ...rules, dateValidation ]"
        :lazy-rules="lazyRules"
        :clearable="false"
        outlined
        stack-label
        @update:modelValue="input"
        @focus="$emit('focus-time')"
      />
      <div
        v-if="useNow"
        class="flex items-center absolute-top-right full-height"
      >
        <q-btn
          class="fs-12 q-mr-12 text-weight-medium"
          dense
          unelevated
          color="grey-4"
          text-color="grey-10"
          padding="2px 12px"
          :label="$t('button.now')"
          rounded
          @click="setNowDate"
        />
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="col-12 text-negative fs-12 q-pt-8"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  inject,
} from 'vue';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';

import DateInput from './DateInput';
import TimeField from './TimeField';

import useFormatDate from '~composables/useFormatDate';

const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
  lazyRules: {
    type: [Boolean, String],
    default: false,
  },
  minDate: {
    type: String,
    default: null,
  },
  maxDate: {
    type: String,
    default: null,
  },
  dateLabel: {
    type: String,
    required: true,
  },
  timeLabel: {
    type: String,
    required: true,
  },
  rules: {
    type: Array,
    default: () => [],
  },
  defaultYearMonth: {
    type: String,
    default: '',
  },
  useNow: {
    type: Boolean,
    default: false,
  },
  combinedDatetimeField: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue', 'focus-date', 'focus-time', 'set-now']);

const { formatDate } = useFormatDate();

const moment = inject('moment');
const $q = useQuasar();
const { t } = useI18n();

const dateRef = ref(null);
const timeRef = ref(null);

const date = ref('');
const time = ref('');
const error = ref({});

const wideClass = computed(() => {
  if (props.combinedDatetimeField) {
    return 'col-12';
  }

  return $q.screen.lt.md ? 'w-150' : 'col-6';
});

const limits = computed(() => ({
  max: props.maxDate && moment.parseZone(props.maxDate),
  min: props.minDate && moment.parseZone(props.minDate === 'now' ? undefined : props.minDate),
}));

const errorMessage = computed(() => {
  if (!error.value.type) return null;
  if (error.value.type === 'required') return t('error.required_field');
  return t(`error.date.${error.value.type}`, { date: error.value.date });
});

function dateValidation() {
  if (props.combinedDatetimeField && date.value && !time.value) {
    error.value = { type: 'required' };
    return false;
  }

  if (props.modelValue) {
    const d = moment(props.modelValue);
    const format = 'YYYY-MM-DD HH:mm';

    if (limits.value.min && d.isBefore(limits.value.min.format(format), 'minute')) {
      error.value = {
        type: 'after',
        date: formatDate(limits.value.min),
      };
      return false;
    }
    if (limits.value.max && d.isAfter(limits.value.max.format(format), 'minute')) {
      error.value = {
        type: 'before',
        date: formatDate(limits.value.max),
      };
      return false;
    }
  }

  error.value = {};
  return true;
}

function getDate(value, format) {
  const val = moment(value, undefined, 'en');
  return val.isValid() ? val.format(format) : '';
}

function input() {
  if (date.value && time.value) {
    emit('update:modelValue', getDate(`${date.value} ${time.value}`, 'YYYY-MM-DDTHH:mm:00Z'));
  } else {
    emit('update:modelValue', '');
  }
}

function onUpdateCombinedTime(value) {
  time.value = value;
  input();
}

function setNowDate() {
  emit('update:modelValue', moment().format('YYYY-MM-DDTHH:mm:00Z'));
  emit('set-now');
}

watch(() => props.modelValue, (value) => {
  if (value) {
    date.value = getDate(value, 'YYYY-MM-DD');
    time.value = getDate(value, 'HH:mm');
    dateRef.value.$refs.inputRef.resetValidation();
    timeRef.value?.$refs.fieldRef.resetValidation();
  }
});

onMounted(() => {
  date.value = getDate(props.modelValue, 'YYYY-MM-DD');
  time.value = getDate(props.modelValue, 'HH:mm');
});
</script>

<style scoped>
.w-150 {
  width: 150px;
}
</style>
